import { useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { DataTableView } from "@/components/organisms/DataTableView";
import { credentialRequestColumns } from "@/models/columnDefinitions/credentialRequestCols";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/atoms/ConfirmationDialog";
import { Plus, Check, X, Printer, Package } from "lucide-react";
import type { CredentialRequest } from "@/models/credentialRequest";
import { useCredentialPermissions } from "../../hooks/useCredentialPermissions";

interface RequestsTableProps {
  requests: CredentialRequest[];
  isLoading: boolean;
  isSubmitting: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onRowClick: (request: CredentialRequest) => void;
  onApprove: (request: CredentialRequest) => void;
  onRejectClick: (request: CredentialRequest) => void;
  onMarkPrinted: (request: CredentialRequest) => void;
  onMarkDelivered: (request: CredentialRequest) => void;
  onCreateNew: () => void;
  columnOptions?: {
    hideDistrict?: boolean;
    hideRegion?: boolean;
  };
}

/**
 * Organism: Requests table with admin action buttons
 * Displays credential requests with pagination, search, sorting, and admin actions
 */
export function RequestsTable({
  requests,
  isLoading,
  isSubmitting,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  sorting,
  onSortingChange,
  onRowClick,
  onApprove,
  onRejectClick,
  onMarkPrinted,
  onMarkDelivered,
  onCreateNew,
  columnOptions = {},
}: RequestsTableProps) {
  const { forRequest, canWorkflowAnywhere } = useCredentialPermissions();

  // Create columns with actions (each button needs its own permission in the request's territory)
  const columnsWithActions: ColumnDef<CredentialRequest>[] = useMemo(
    () => [
      ...credentialRequestColumns(columnOptions),
      ...(canWorkflowAnywhere
        ? [
            {
              id: "actions",
              header: "Acciones",
              cell: ({ row }: { row: { original: CredentialRequest } }) => {
                const request = row.original;
                const perms = forRequest(request);

                return (
                  <div 
                    className="flex gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {request.status === "SUBMITTED" && (perms.canApprove || perms.canReject) && (
                      <>
                        {perms.canApprove && (
                        <ConfirmationDialog
                          title="Confirmar Aprobación"
                          description={`¿Está seguro que desea aprobar la solicitud ${request.code}? Esta acción no se puede deshacer.`}
                          confirmText="Aprobar"
                          onConfirm={() => onApprove(request)}
                        >
                          <Button
                            size="sm"
                            variant="default"
                            disabled={isSubmitting}
                            title="Aprobar"
                            className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
                          >
                            <Check className="size-4" />
                          </Button>
                        </ConfirmationDialog>
                        )}
                        {perms.canReject && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRejectClick(request);
                          }}
                          disabled={isSubmitting}
                          title="Observar"
                        >
                          <X className="size-4" />
                        </Button>
                        )}
                      </>
                    )}
                    {request.status === "APPROVED" && perms.canMarkPrinted && (
                      <ConfirmationDialog
                        title="Confirmar Impresión"
                        description={`¿Confirma que las credenciales de la solicitud ${request.code} han sido impresas?`}
                        confirmText="Marcar como Impreso"
                        onConfirm={() => onMarkPrinted(request)}
                      >
                        <Button
                          size="sm"
                          variant="default"
                          disabled={isSubmitting}
                          title="Marcar como impreso"
                          className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700"
                        >
                          <Printer className="size-4" />
                        </Button>
                      </ConfirmationDialog>
                    )}
                    {request.status === "PRINTED" && perms.canMarkDelivered && (
                      <ConfirmationDialog
                        title="Confirmar Entrega"
                        description={`¿Confirma que las credenciales de la solicitud ${request.code} han sido entregadas?`}
                        confirmText="Marcar como Entregado"
                        onConfirm={() => onMarkDelivered(request)}
                      >
                        <Button
                          size="sm"
                          variant="default"
                          disabled={isSubmitting}
                          title="Marcar como entregado"
                          className="bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-700"
                        >
                          <Package className="size-4" />
                        </Button>
                      </ConfirmationDialog>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    [forRequest, canWorkflowAnywhere, isSubmitting, onApprove, onRejectClick, onMarkPrinted, onMarkDelivered]
  );

  const toolbarActions = (
    <Button onClick={onCreateNew} className="flex items-center gap-2">
      <Plus className="size-4" />
      Nueva Solicitud
    </Button>
  );

  return (
    <DataTableView<CredentialRequest>
      data={requests}
      columns={columnsWithActions}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder="Buscar por código o notas de solicitud, o código de ministro..."
      sorting={sorting}
      onSortingChange={onSortingChange}
      toolbarActions={toolbarActions}
      onRowClick={onRowClick}
      isLoading={isLoading}
      emptyMessage="No se encontraron solicitudes de credenciales"
    />
  );
}
